class CustomContactForm extends HTMLElement {
    constructor() {
        super();

        this.form = this.querySelector('form');
        this.fileUpload = this.querySelectorAll('input[type=file]');
        this.submitButton = this.querySelector('button[type=submit]');
    }

    get useCustomMailer() {
        return this.dataset.customMailer === "true";
    }

    connectedCallback() {
        this.#validationListeners();

        // Skip custom email handling if data-handler attribute is not set to "true"
        if(!this.useCustomMailer) return;

        if(this.form) {
            this.form.removeAttribute('action');
            this.form.addEventListener('submit', this.#onSubmit.bind(this));
        }

        if(this.fileUpload) {
            this.fileUpload.forEach((input) => {
                input.addEventListener('change', (event) => {
                    this.filesArray = this.filesArray || [];
                    Array.from(event.target.files).forEach((file) => {
                        this.filesArray.push(file);
                        this.#addUploadedFile(file);
                    });
                });
            });
        }
    }

    #validationListeners() {
        if(this.form && this.submitButton) {
            this.form.addEventListener('invalid', (e) => {
                // This prevents the browser popup
                e.preventDefault();

                if (!e.target) return;

                // Set aria-invalid and aria-describedby for screen readers
                e.target.setAttribute('aria-invalid', 'true');
                e.target.classList.add('error');
                
                // Find the associated error message element
                const errorEl = this.#getErrorElement(e.target);
                if (!errorEl) return;

                e.target.setAttribute('aria-describedby', errorEl.id);

                errorEl.classList.remove('hidden'); // Ensure the error message is visible
                errorEl.setAttribute('aria-hidden', 'false'); // now visible to screen readers
                errorEl.setAttribute('role', 'alert'); // optional: announce immediately
            }, true); // useCapture = true is important!

            // Clear aria-invalid when user corrects the input
            this.form.querySelectorAll('input, textarea, select').forEach(input => {
                input.addEventListener('input', () => {
                    if (!input.checkValidity()) return;

                    // Remove error message and aria attributes if the input is valid
                    input.removeAttribute('aria-invalid');
                    input.removeAttribute('aria-describedby');
                    input.classList.remove('error');

                    // Find the associated error message element
                    const errorEl = this.#getErrorElement(input);
                    if (!errorEl) return;

                    errorEl.classList.add('hidden');
                    errorEl.setAttribute('aria-hidden', 'true');
                    errorEl.removeAttribute('role');
                });
            });
        }
    }

    
    #getErrorElement(input) {
        const errorById = input.id ? document.getElementById(`${input.id}-error`) : null;
        const errorByName = input.name ? document.getElementById(`${input.name}-error`) : null;
        return errorById || errorByName;
    }

    #onSubmit(event) {
        event.preventDefault();
        
        if(this.submitButton) {
            this.submitButton.setAttribute("aria-busy", "true");
        }

        const formData = this.#createFormData();
        
        const formDataObj = {};
        for (let [key, value] of formData.entries()) {
            formDataObj[key] = value;
        }

        fetch("/apps/sinoplasan-app/handler-registration", {
            method: "POST",
            body: formData,
        })
        .then((response) => {
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const contactForm = this.querySelector('.contact-form');
            if (contactForm) {
                contactForm.classList.add('hidden');
                contactForm.setAttribute('aria-hidden', 'true');
            }

            const successMessage = this.querySelector('.success-message');
            if (successMessage) {
                successMessage.classList.remove('hidden');
                successMessage.setAttribute('aria-hidden', 'false');
                successMessage.setAttribute('role', 'status');

                const rect = successMessage.getBoundingClientRect();
                const scrollTop = window.scrollY || document.documentElement.scrollTop;
                const offset = rect.top + scrollTop - (window.innerHeight / 2) + (rect.height / 2);
                document.documentElement.scrollTo({ top: offset, behavior: 'smooth' });
            }
        })
        .catch((error) => {
            console.error("Error submitting form:", error);
        })
        .finally(() => {
            if(this.submitButton) {
                this.submitButton.removeAttribute("aria-busy");
            }
        });
    }

    #addUploadedFile(file){
        const parent = this.querySelector('.uploaded-files');
        if(parent){
            parent.classList.remove('hidden');
            const p = document.createElement('p');
            p.textContent = file.name;
            parent.appendChild(p);
        }

    }

    #createFormData() {
        const formInputs = this.querySelectorAll('[data-label]');
        let requestBody = {};
    
        // ADD RECIPIENT
        const recipientEmail = this.querySelector("input[name='recipient_email'")?.value;
        if (recipientEmail) {
            requestBody = {
                ...requestBody,
                recipient: recipientEmail 
            };
        }
    
        // ADD SUBJECT
        const subject = this.querySelector("input[name='email_subject'")?.value;
        if(subject){
            requestBody = {
                ...requestBody,
                subject
            };
        } else {
            requestBody = {
                ...requestBody,
                subject: "Neue Registrierung"
            };
        }
    
        // ADD CC (NOT NEEDED ATM)
        requestBody = {
            ...requestBody,
            cc: []
        };
    
        // ADD INPUTS ARRAY
        const inputs = [];
    
        for (const formInput of formInputs){
            const name = formInput.getAttribute('name');
            if (name === 'email_subject' || name === 'recipient_email') {
                continue;
            }
            
            const label = formInput.getAttribute('data-label');
            const trimmedLabel = label?.trim().replace(/\*$/, '');

            if (label && formInput.value) {
                inputs.push({ labelName: trimmedLabel, input: formInput.value });
            }
        }

        requestBody = {
            ...requestBody,
            inputs
        };

        const finalFormData = new FormData();
        finalFormData.append("requestBody", JSON.stringify(requestBody));

        // ADD FILES HERE
        if(this.filesArray){
            Array.from(this.filesArray).forEach((file)=> {
                const fileBlob = new Blob([file], {type: file.type});
                finalFormData.append('files[]', fileBlob, file.name);
            })
        }
        
        return finalFormData;
    }
}

if (!customElements.get('custom-contact-form')) customElements.define("custom-contact-form", CustomContactForm);
